<template>
  <div class="setting-gallery-item">
    <!-- 设定信息区 -->
    <div class="setting-info">
      <div class="setting-header">
        <h5 class="setting-name">{{ settingName }}</h5>
        <el-tag size="small" :type="getTypeTag(settingType)">
          {{ getTypeLabel(settingType) }}
        </el-tag>
      </div>

      <!-- 可编辑字段 -->
      <div class="editable-fields">
        <!-- 角色：外貌、性格 -->
        <template v-if="settingType === 'character'">
          <div class="field-item">
            <label>外貌特征</label>
            <el-input
              v-model="editableData.appearance"
              type="textarea"
              :rows="2"
              placeholder="描述角色的外貌特征"
              @blur="saveField('appearance')"
            />
          </div>
          <div class="field-item">
            <label>性格特点</label>
            <el-input
              v-model="editableData.personality"
              type="textarea"
              :rows="2"
              placeholder="描述角色的性格特点"
              @blur="saveField('personality')"
            />
          </div>
        </template>

        <!-- 地点：描述 -->
        <template v-if="settingType === 'location'">
          <div class="field-item">
            <label>地点描述</label>
            <el-input
              v-model="editableData.description"
              type="textarea"
              :rows="3"
              placeholder="描述地点的特征"
              @blur="saveField('description')"
            />
          </div>
        </template>

        <!-- 道具：描述 -->
        <template v-if="settingType === 'prop'">
          <div class="field-item">
            <label>道具描述</label>
            <el-input
              v-model="editableData.description"
              type="textarea"
              :rows="3"
              placeholder="描述道具的特征"
              @blur="saveField('description')"
            />
          </div>
        </template>

        <!-- 世界观：时代、视觉风格 -->
        <template v-if="settingType === 'worldview'">
          <div class="field-item">
            <label>时代背景</label>
            <el-input
              v-model="editableData.era"
              placeholder="描述世界观的时代背景"
              @blur="saveField('era')"
            />
          </div>
          <div class="field-item">
            <label>视觉风格</label>
            <el-input
              v-model="editableData.visual_style"
              placeholder="描述世界观的视觉风格"
              @blur="saveField('visual_style')"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- 图片生成区 -->
    <div class="image-section">
      <div class="generate-controls">
        <span class="generate-label">生成数量：</span>
        <el-select v-model="generateCount" size="small" style="width: 80px">
          <el-option label="1张" :value="1" />
          <el-option label="2张" :value="2" />
          <el-option label="3张" :value="3" />
        </el-select>
        <el-button
          type="primary"
          size="small"
          :loading="generating"
          :disabled="!selectedStyle"
          @click="handleGenerate"
        >
          <el-icon><MagicStick /></el-icon>
          生成设定图
        </el-button>
      </div>

      <!-- 图片缩略图列表 -->
      <div v-if="images.length > 0" class="image-thumbnails">
        <div
          v-for="(image, index) in images"
          :key="image.id"
          class="thumbnail-item"
          :class="{ 'is-default': image.is_default }"
          @click="handlePreview(index)"
        >
          <img :src="image.image_url" :alt="settingName">
          <div v-if="image.is_default" class="default-badge">
            <el-icon><Check /></el-icon>
            默认
          </div>
          <div class="thumbnail-overlay">
            <el-button type="primary" link size="small" @click.stop="handlePreview(index)">
              <el-icon><View /></el-icon>
              查看
            </el-button>
            <el-button type="danger" link size="small" @click.stop="handleDelete(image.id)">
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-images">
        <el-icon :size="32"><Picture /></el-icon>
        <p>尚未生成设定图</p>
        <p class="hint">选择风格后点击生成按钮</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { MagicStick, Check, View, Picture, Delete } from '@element-plus/icons-vue'

const props = defineProps({
  setting: {
    type: Object,
    required: true
  },
  settingType: {
    type: String,
    required: true
  },
  selectedStyle: {
    type: String,
    default: ''
  },
  images: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update-field', 'generate', 'set-default', 'preview', 'delete'])

// 状态
const generateCount = ref(1)
const generating = ref(false)

// 解析设定值
const settingValue = computed(() => {
  try {
    return JSON.parse(props.setting.setting_value || '{}')
  } catch {
    return {}
  }
})

const settingName = computed(() => settingValue.value.name || '未命名设定')

// 可编辑数据
const editableData = ref({
  appearance: settingValue.value.appearance || '',
  personality: settingValue.value.personality || '',
  description: settingValue.value.description || '',
  era: settingValue.value.era || '',
  visual_style: settingValue.value.visual_style || ''
})

// 监听设定值变化
watch(() => props.setting.setting_value, (newVal) => {
  try {
    const value = JSON.parse(newVal || '{}')
    editableData.value.appearance = value.appearance || ''
    editableData.value.personality = value.personality || ''
    editableData.value.description = value.description || ''
    editableData.value.era = value.era || ''
    editableData.value.visual_style = value.visual_style || ''
  } catch {
    // 解析失败时保持原值
  }
}, { deep: true })

// 方法
const getTypeLabel = (type) => {
  const labels = {
    character: '角色',
    location: '地点',
    prop: '道具',
    worldview: '世界观'
  }
  return labels[type] || type
}

const getTypeTag = (type) => {
  const types = {
    character: 'success',
    location: 'warning',
    prop: 'info',
    worldview: 'danger'
  }
  return types[type] || ''
}

const saveField = (fieldName) => {
  const value = editableData.value[fieldName]
  const originalValue = settingValue.value[fieldName]

  // 只有在值变化时才保存
  if (value !== originalValue) {
    emit('update-field', props.setting.id, { [fieldName]: value })
  }
}

const handleGenerate = () => {
  if (!props.selectedStyle) {
    ElMessage.warning('请先选择视觉风格')
    return
  }
  emit('generate', props.setting.id, generateCount.value)
}

const handlePreview = (index) => {
  emit('preview', props.images, index, settingName.value)
}

const handleDelete = (imageId) => {
  emit('delete', imageId, props.setting.id)
}
</script>

<style scoped lang="scss">
.setting-gallery-item {
  display: flex;
  gap: 20px;
  padding: 16px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-light);

  .setting-info {
    flex: 1;
    min-width: 0;

    .setting-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;

      .setting-name {
        font-size: 16px;
        font-weight: 600;
        color: var(--el-text-color-primary);
        margin: 0;
      }
    }

    .editable-fields {
      .field-item {
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        label {
          display: block;
          font-size: 13px;
          color: var(--el-text-color-regular);
          margin-bottom: 6px;
        }

        .el-input,
        .el-textarea {
          width: 100%;
        }
      }
    }
  }

  .image-section {
    width: 280px;
    flex-shrink: 0;

    .generate-controls {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding: 12px;
        background: var(--el-bg-color);
        border-radius: 6px;

        .generate-label {
          font-size: 13px;
          color: var(--el-text-color-regular);
        }

      .el-button {
        margin-left: auto;
      }
    }

    .image-thumbnails {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;

      .thumbnail-item {
        position: relative;
        aspect-ratio: 1;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.3s;

        &:hover {
          border-color: #409eff;

          .thumbnail-overlay {
            opacity: 1;
          }
        }

        &.is-default {
          border-color: #67c23a;
          box-shadow: 0 0 0 2px rgba(103, 194, 58, 0.2);
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .default-badge {
          position: absolute;
          top: 4px;
          left: 4px;
          display: flex;
          align-items: center;
          gap: 2px;
          padding: 2px 6px;
          background: #67c23a;
          color: #fff;
          font-size: 11px;
          border-radius: 4px;

          .el-icon {
            font-size: 12px;
          }
        }

        .thumbnail-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          transition: opacity 0.3s;
        }
      }
    }

    .empty-images {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: var(--el-bg-color);
      border-radius: 6px;
      color: var(--el-text-color-secondary);

      p {
        margin: 8px 0 0;
        font-size: 13px;

        &.hint {
          font-size: 12px;
          color: var(--el-text-color-placeholder);
        }
      }
    }
  }
}
</style>
